<template>
  <side-bar v-if="open" class="project-settings__container">
    <div class="project-settings">
      <menu-group class="itemheader">
        <h1 class="itemheader__title">
          {{ $t("headings.projectSettings") }}
        </h1>
        <button
          type="button"
          class="button-icon itemheader__closebutton"
          @click="open = false"
        >
          <lucide-icon name="x" />
        </button>
        <div class="project-settings__appversion">
          {{ $t("labels.versionX", [appVersion]) }}
        </div>
      </menu-group>

      <drawer :title="$t('headings.activeFeatures')">
        <div class="column">
          <div class="column gap-1 mt-2">
            <div v-for="(enabled, feature) in standardFeatures" :key="feature">
              <input-switch
                :label="
                  te(`objectNames.${feature}`)
                    ? $t(`objectNames.${feature}`, 4)
                    : te(`labels.${feature}`)
                    ? $t(`labels.${feature}`)
                    : feature
                "
                :disabled="featuresInUse[feature]"
                :label-right="true"
                :value="enabled"
                @update:value="featureStore.set(feature, $event)"
              />
            </div>
          </div>

          <details v-if="betaEnabled && Object.values(betaFeatures).length > 0">
            <summary>{{ $t("labels.betaFeatures") }}</summary>
            <div
              class="message message-info mt-2"
              style="
                padding: 1rem;
                background-color: var(--color-warning-light);
                border: 2px solid var(--color-warning);
                border-radius: 0.5rem;
              "
            >
              {{ $t("texts.betaWarning") }}
              <p class="mb-0 mt-2">
                <strong>{{ $t("texts.useAtOwnRisk") }}</strong>
              </p>
            </div>
            <button
              v-if="!featureStore.betaFeaturesActive"
              type="button"
              class="button mt-2"
              @click="featureStore.activateBetaFeatures()"
            >
              {{ $t("actions.activateBetaFeatures") }}
            </button>
            <button
              v-if="featureStore.betaFeaturesActive"
              type="button"
              class="button mt-2"
              @click="featureStore.deactivateBetaFeatures()"
            >
              {{ $t("actions.deactivateBetaFeatures") }}
            </button>
            <div class="column gap-1 mt-2">
              <div v-for="(enabled, feature) in betaFeatures" :key="feature">
                <input-switch
                  :label="
                    te(`objectNames.${(feature as string)}`)
                      ? $t(`objectNames.${(feature as string)}`, 4)
                      : te(`labels.${(feature as string)}`)
                      ? $t(`labels.${(feature as string)}`)
                      : feature
                  "
                  :disabled="featuresInUse[feature]"
                  :label-right="true"
                  :value="enabled"
                  @update:value="featureStore.set(feature, $event)"
                />
              </div>
            </div>
          </details>
        </div>
      </drawer>

      <drawer :title="$t('labels.editorAreas')">
        <div class="column gap-2">
          <input-checkbox
            v-for="area in settings.availableAreas"
            :key="area.id"
            :label="areaNames[area.id]"
            :value="!settings.hiddenAreas.includes(area.id)"
            @update:value="setAreaVisibility(area.id, $event)"
          />
        </div>
      </drawer>

      <drawer :title="$t('labels.design')">
        <input-color
          :value="settings.colorPrimary"
          default-value="var(--color-primary)"
          @update:value="settings.setPrimaryColor($event)"
        >
          {{ $t("labels.colorAction") }}
        </input-color>

        <drawer
          :nested="true"
          :title="$t('labels.extraColors')"
          style="border-bottom: none"
          class="mt-2 mb-2"
        >
          <div class="message mb-2">
            {{ $t("texts.colorExplanation") }}
          </div>
          <div class="column gap-1">
            <input-color
              :value="settings.colorPrimaryLight"
              default-value="var(--color-primary-light)"
              @update:value="setDerivedColor('colorPrimaryLight', $event)"
            >
              {{ $t("labels.colorActionLight") }}
            </input-color>
            <input-color
              :value="settings.colorPrimaryDark"
              default-value="var(--color-primary-dark)"
              @update:value="setDerivedColor('colorPrimaryDark', $event)"
            >
              {{ $t("labels.colorActionDark") }}
            </input-color>

            <input-color
              class="mt-4"
              :value="settings.colorGreyLighter"
              default-value="var(--color-grey-lighter)"
              @update:value="setDerivedColor('colorGreyLighter', $event)"
            >
              {{ $t("labels.colorGreyLighter") }}
            </input-color>
            <input-color
              :value="settings.colorGreyLight"
              default-value="var(--color-grey-light)"
              @update:value="setDerivedColor('colorGreyLight', $event)"
            >
              {{ $t("labels.colorGreyLight") }}
            </input-color>
            <input-color
              :value="settings.colorGrey"
              default-value="var(--color-grey)"
              @update:value="setDerivedColor('colorGrey', $event)"
            >
              {{ $t("labels.colorGrey") }}
            </input-color>
            <input-color
              :value="settings.colorGreyDark"
              default-value="var(--color-grey-dark)"
              @update:value="setDerivedColor('colorGreyDark', $event)"
            >
              {{ $t("labels.colorGreyDark") }}
            </input-color>
            <input-color
              :value="settings.colorGreyDarker"
              default-value="var(--color-grey-darker)"
              @update:value="setDerivedColor('colorGreyDarker', $event)"
            >
              {{ $t("labels.colorGreyDarker") }}
            </input-color>
          </div>
        </drawer>

        <h3 class="label mt-2">{{ $t("headings.darkMode") }}</h3>
        <div class="column mb-4 mt-1">
          <radio-button
            :value="settings.darkModeOverride"
            :data="null"
            :label="$t('labels.auto')"
            @update:value="setDarkMode($event)"
          />
          <radio-button
            :value="settings.darkModeOverride"
            :data="true"
            :label="$t('labels.activated')"
            @update:value="setDarkMode($event)"
          />
          <radio-button
            :value="settings.darkModeOverride"
            :data="false"
            :label="$t('labels.deactivated')"
            @update:value="setDarkMode($event)"
          />
        </div>

        <fieldset class="menu-fieldset mt-6">
          <legend class="label">{{
            $t("labels.colorsForElementState")
          }}</legend>
          <input-number
            :label="$t('labels.colorAmount')"
            :value="targetValueAreaCount"
            @update:value="targetValueAreaCount = $event"
          />
          <div class="project-settings__valueareas mt-1">
            <color-picker
              v-if="state.choosingColorIndex != null"
              class="project-settings__picker"
              :value="state.chosenColor"
              v-on-click-outside="() => state.choosingColorIndex = null"
              @update:value="chooseColor($event as string)"
            />
            <button
              v-for="(color, index) in targetValueColors"
              :key="'color' + index"
              :style="{ backgroundColor: color }"
              class="project-settings__valuearea"
              @click="togglePicker(index, color)"
            ></button>
          </div>
          <template v-if="customColorsActive">
            <button
              type="button"
              class="button mt-2"
              @click="customColorsActive = false"
            >
              {{ $t("labels.resetColors") }}
            </button>
            <button
              type="button"
              class="button mt-2"
              @click="regenerateElementValueColors()"
            >
              {{ $t("actions.generateColorScale") }}
            </button>
          </template>
        </fieldset>
      </drawer>

      <drawer :title="$t('objectNames.impact', 2)">
        <fieldset class="menu-fieldset mb-4">
          <legend class="label">{{ $t("labels.relationStrengthPresets") }}</legend>

          <div class="line_flex">
            <input-number
              :label="$t('relationStrengths.weak')"
              :value="relationStrengthPresets.weak"
              step="any"
              @update:value="
                settings.set('relationStrengthPresets', {
                  ...relationStrengthPresets,
                  weak: $event
                })
              "
            />
            <input-number
              :label="$t('relationStrengths.medium')"
              :value="relationStrengthPresets.medium"
              step="any"
              @update:value="
                settings.set('relationStrengthPresets', {
                  ...relationStrengthPresets,
                  medium: $event
                })
              "
            />
            <input-number
              :label="$t('relationStrengths.strong')"
              :value="relationStrengthPresets.strong"
              step="any"
              @update:value="
                settings.set('relationStrengthPresets', {
                  ...relationStrengthPresets,
                  strong: $event
                })
              "
            />
          </div>
        </fieldset>

        <input-switch
          v-model:value="isPathRoundingEnabled"
          :label="$t('labels.pathRounding')"
        />
        <input-switch
          v-model:value="isPathSpacingEnabled"
          :label="$t('labels.pathSpacing')"
        />
        <input-switch
          v-model:value="isPathDeoverlappingEnabled"
          :label="$t('labels.pathDeoverlapping')"
        />

        <hr />

        
        <fieldset class="menu-fieldset mt-6">
          <!-- <h4 class="label mt-4 mb-0">{{ $t("headings.colors") }}</h4> -->
          <legend class="label">{{ $t("headings.colors") }}</legend>
          <div class="list">
            <input-color
              :value="relationColors.standard ?? undefined"
              default-value="var(--color-relation-standard)"
              @update:value="settings.setColor('colorRelationStandard', $event)"
            >
              {{ $t("labels.relationColorStandard") }}
            </input-color>

            <input-color
              :value="relationColors.active ?? undefined"
              default-value="var(--color-action)"
              @update:value="settings.setColor('colorRelationActive', $event)"
            >
              {{ $t("labels.relationColorActive") }}
            </input-color>
          </div>
          <drawer
            :title="$t('headings.inverseRelationColors')"
            :nested="true"
            class="mt-2"
          >
            <div class="column">
              <input-color
                :value="relationColors.inverse ?? undefined"
                :default-value="
                  relationColors.standard ?? 'var(--color-relation-inverse)'
                "
                @update:value="settings.setColor('colorRelationInverse', $event)"
              >
                {{ $t("labels.relationColorStandard") }}
              </input-color>
  
              <input-color
                :value="relationColors.inverseActive ?? undefined"
                :default-value="relationColors.active ?? 'var(--color-action)'"
                @update:value="
                  settings.setColor('colorRelationInverseActive', $event)
                "
              >
                {{ $t("labels.relationColorActive") }}
              </input-color>
            </div>
          </drawer>
  
          <drawer
            :title="$t('headings.inOutRelationColors')"
            :nested="true"
            style="border-bottom: none"
          >
            <div class="column">
              <div class="message mb-2 mt-3">
                {{ $t("texts.extraColorsExplanation") }}
              </div>
              <input-color
                :value="relationColors.incoming ?? undefined"
                :default-value="relationColors.active ?? 'var(--color-action)'"
                @update:value="settings.setColor('colorRelationIncoming', $event)"
              >
                {{ $t("labels.relationColorIncoming") }}
              </input-color>
  
              <input-color
                :value="relationColors.outgoing ?? undefined"
                :default-value="relationColors.active ?? 'var(--color-action)'"
                @update:value="settings.setColor('colorRelationOutgoing', $event)"
              >
                {{ $t("labels.relationColorOutgoing") }}
              </input-color>
  
              <input-color
                :value="relationColors.connecting ?? undefined"
                :default-value="relationColors.active ?? 'var(--color-action)'"
                @update:value="
                  settings.setColor('colorRelationConnecting', $event)
                "
              >
                {{ $t("labels.relationColorConnecting") }}
              </input-color>
  
              <div class="message mb-2 mt-3">
                {{ $t("headings.extraInverseRelationColors") }}
              </div>
  
              <input-color
                :value="relationColors.inverseIncoming ?? undefined"
                :default-value="
                  relationColors.inverseActive ??
                  relationColors.incoming ??
                  relationColors.active ??
                  'var(--color-action)'
                "
                @update:value="
                  settings.setColor('colorRelationInverseIncoming', $event)
                "
              >
                {{ $t("labels.relationColorIncoming") }}
              </input-color>
  
              <input-color
                :value="relationColors.inverseOutgoing ?? undefined"
                :default-value="
                  relationColors.inverseActive ??
                  relationColors.outgoing ??
                  relationColors.active ??
                  'var(--color-action)'
                "
                @update:value="
                  settings.setColor('colorRelationInverseOutgoing', $event)
                "
              >
                {{ $t("labels.relationColorOutgoing") }}
              </input-color>
  
              <input-color
                :value="relationColors.inverseConnecting ?? undefined"
                :default-value="
                  relationColors.inverseActive ??
                  relationColors.connecting ??
                  relationColors.active ??
                  'var(--color-action)'
                "
                @update:value="
                  settings.setColor('colorRelationInverseConnecting', $event)
                "
              >
                {{ $t("labels.relationColorConnecting") }}
              </input-color>
            </div>
          </drawer>
        </fieldset>


        <!--
            <input-switch
            :label="$t('labels.extraInversionRelationColors')"
            :value="settings.extraInverseRelationColors"
            :label-right="true"
            class="mt-2"
            @update:value="settings.set('extraInverseRelationColors', $event)"
          />
            <div v-if="settings.extraInverseRelationColors" class="list">
            <h5 class="label mt-0">{{ $t("labels.inverseRelationColors") }}</h5>



          </div>
          -->
      </drawer>

      <menu-group style="display: none">
        <h3>{{ $t('labels.devOptions') }}</h3>
        <label class="label">{{ $t("labels.showDebugInfo") }}</label>
        <input-switch
          :value="developerMode"
          @update:value="developerMode = $event"
        />
      </menu-group>

      <menu-group v-if="developerMode">
        <UndoProtocol />
      </menu-group>
    </div>
  </side-bar>
</template>

<script setup lang="ts">
import { reactive, computed } from "vue";
import { useI18n } from "vue-i18n";
import { vOnClickOutside } from '@vueuse/components';
import envVars from "@/helpers/Env";

import SideBar from "@core/ui/SideBar.vue";
import MenuGroup from "@core/ui/MenuGroup.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

import InputSwitch from "@core/input/InputSwitch.vue";
import InputNumber from "@core/input/InputNumber.vue";
import Drawer from "@core/ui/DrawerBlock.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";
import ColorPicker from "@core/input/ColorPicker.vue";
import InputColor from "@core/input/InputColor.vue";
import RadioButton from "@core/input/RadioButton.vue";
import UndoProtocol from "./UndoProtocol.vue";

import { useUser } from "@/api/user.store";
import { useSettingsPanel } from "@core/ui/settings.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useFeatureStore } from "@editor/store/features.store";
import { useColor } from "@/helpers/utility/Color";

import type { ColorName } from "@editor/store/settings.store";
import type { EditorAreaName } from "@editor/store/editorAreas";

const state = reactive({
  question: {
    description: ""
  },
  chosenColor: "#fff",
  choosingColorIndex: null as number | null
});

const settings = useSettingsStore();
const featureStore = useFeatureStore();
const { t, te } = useI18n();
const { user } = useUser();

const appVersion = computed(() => {
  return envVars.version;
});

const open = computed({
  get() {
    return useSettingsPanel().settingsPaneOpen;
  },
  set(val: boolean) {
    useSettingsPanel().setSettingsPaneOpen(val);
  }
});

const developerMode = computed({
  get() {
    return settings.developerMode;
  },
  set(val: boolean) {
    localStorage.setItem("developerModeActive", JSON.stringify(val));
    settings.set("developerMode", val);
  }
});


const customColorsActive = computed({
  get() {
    return settings.customTargetValueAreaColors;
  },
  set(val: boolean) {
    if (val === false) {
      settings.resetValueAreaColors();
    } else {
      settings.set("customTargetValueAreaColors", true);
    }
  }
});

const isPathRoundingEnabled = computed({
  get() {
    return useRelationStore().roundPaths;
  },
  set(value: boolean) {
    return useRelationStore().setState({
      roundPaths: value
    });
  }
});

const isPathSpacingEnabled = computed({
  get() {
    return useRelationStore().spacePaths;
  },
  set(value: boolean) {
    return useRelationStore().setState({
      spacePaths: value
    });
  }
});

const isPathDeoverlappingEnabled = computed({
  get() {
    return useRelationStore().deoverlapPaths;
  },
  set(value: boolean) {
    return useRelationStore().setState({
      deoverlapPaths: value
    });
  }
});

const relationStrengthPresets = computed(() => {
  return settings.relationStrengthPresets;
});

const areaNames = computed(() => {
  return {
    model: t("areas.network"),
    variables: t("areas.variables"),
    inspector: t("areas.analysis"),
    simulation: t("areas.simulation"),
    evaluation: t("areas.evaluation")
  };
});

const betaEnabled = computed(() => envVars.isDev || user.value?.isBetaEnabled);

const standardFeatures = computed(() => featureStore.standardFeatures);
const betaFeatures = computed(() => featureStore.betaFeatures);
const featuresInUse = computed(() => featureStore.featuresInUse);

const relationColors = computed(() => {
  return {
    standard: settings.colorRelationStandard,
    active: settings.colorRelationActive,
    incoming: settings.colorRelationIncoming,
    outgoing: settings.colorRelationOutgoing,
    connecting: settings.colorRelationConnecting,

    inverse: settings.colorRelationInverse,
    inverseActive: settings.colorRelationInverseActive,
    inverseIncoming: settings.colorRelationInverseIncoming,
    inverseOutgoing: settings.colorRelationInverseOutgoing,
    inverseConnecting: settings.colorRelationInverseConnecting
  };
});

function setDerivedColor(colorName: ColorName, value: string | null) {
  if (!value || value.startsWith("var")) {
    settings.regenerateColors([colorName]);
  } else {
    settings.setColor(colorName, value);
  }
}

function togglePicker(index: number, color: string) {
  if (state.choosingColorIndex === index) {
    state.choosingColorIndex = null;
    return;
  }
  state.choosingColorIndex = index;
  state.chosenColor = color;
}

function setAreaVisibility(areaId: EditorAreaName, visible: boolean) {
  const hiddenAreas = settings.hiddenAreas;
  
  if (visible) {
    settings.set(
      "hiddenAreas",
      hiddenAreas.filter((aId) => aId !== areaId)
    );
  } else {
    settings.set("hiddenAreas", [...hiddenAreas, areaId]);
  }
}

// =========================================
// DARK MODE

const { setDark } = useColor();

function setDarkMode(value: boolean | null) {
  setDark(value);
  settings.set("darkModeOverride", value);
}

// =========================================
// TARGET VALUE AREA COLORS


const targetValueAreaCount = computed({
  get() {
    return useSettingsStore().targetValueAreaCount;
  },
  set(val: number) {
    useSettingsStore().setTargetValueAreaCount(val);
  }
});

const targetValueColors = computed(() => settings.targetValueColors);

function chooseColor(color: string) {
  if (state.choosingColorIndex == null) {
    console.warn("not choosing any color.");
    return;
  }

  settings.setTargetValueAreaColor({
    index: state.choosingColorIndex,
    color: color
  });
}

function regenerateElementValueColors() {
  settings.generateValueAreaColors();
}
</script>

<style lang="scss">
.project-settings {
  text-align: left;
  position: relative;
  z-index: 2;

  &__container {
    position: absolute !important;
    right: 0;
    top: 0;
    height: 100%;
    z-index: 2;
  }

  &__appversion {
    font-size: 13px;
    margin-top: 0.25rem;
    color: var(--color-text-medium);
  }

  &__valueareas {
    display: flex;
    position: relative;
  }

  &__valuearea {
    // should not be positioned relatively,
    // to preserve color picker position.
    width: 100%;
    min-height: 2rem;
    appearance: none;
    border: none;
  }

  &__picker {
    position: absolute;
    bottom: 100%;
    left: 0;
    padding-left: 5px;
    background-color: var(--color-background-close);
    box-shadow: 0 1px 4px var(--color-shadow-2);
  }
}
</style>
